import React from 'react';
import { Language } from '../types/kundali';
import { AdvancedLifeAnalysisResult, BhavaAssessmentDetail, KarakaAssessmentDetail } from '../types/advancedLifeAnalysis';
import { Award, Shield, CheckCircle2, AlertTriangle, Info, Sparkles, Activity, Layers } from 'lucide-react';

interface AstrologicalEvidenceGridProps {
  analysisResult: AdvancedLifeAnalysisResult;
  lang: Language;
  className?: string;
}

export const AstrologicalEvidenceGrid: React.FC<AstrologicalEvidenceGridProps> = ({
  analysisResult,
  lang,
  className = ''
}) => {
  const isHi = lang === 'hi';
  const { bhavaAssessments, karakaAssessments, shadbalaSummary, ashtakavargaSummary, supportingFactors } = analysisResult;

  return (
    <div
      id="astrological-evidence-grid"
      className={`bg-white dark:bg-astro-secondary border border-astro-border dark:border-astro-strong-border rounded-2xl p-5 sm:p-6 shadow-sm space-y-4 ${className}`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-astro-border dark:border-astro-strong-border pb-3">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-500" />
          <h3 className="text-sm sm:text-base font-bold uppercase tracking-wider text-astro-text dark:text-astro-muted">
            {isHi ? 'शास्त्रीय ज्योतिषीय प्रमाण योजना (त्रि-स्तंभीय विश्लेषण)' : 'Categorical Astrological Evidence Grid (3-Column Analysis)'}
          </h3>
        </div>
        <span className="text-xs text-astro-muted dark:text-astro-muted">
          {isHi ? 'भाव, ग्रह बल एवं अष्टकवर्ग समन्वय' : 'Bhavas, Planetary Dignity & Ashtakavarga Synthesis'}
        </span>
      </div>

      {/* 3-Column Evidence Layout (Desktop: 3 cols, Tablet: 2 cols, Mobile: 1 col) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
        {/* Column 1: Bhava Conditions & Lord Placement */}
        <div className="p-4 rounded-xl bg-astro-secondary-surface/70 dark:bg-astro-primary/40 border border-astro-border/80 dark:border-astro-strong-border/80 space-y-3">
          <div className="flex items-center gap-1.5 border-b border-astro-border dark:border-astro-strong-border pb-2">
            <Layers className="w-4 h-4 text-amber-500" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text dark:text-astro-muted">
              {isHi ? '१. भाव व भावेश स्थिति' : '1. Bhava & Lord Placements'}
            </h4>
          </div>

          <div className="space-y-2.5">
            {bhavaAssessments.slice(0, 3).map((b) => (
              <div
                key={b.house}
                className="p-3 rounded-lg bg-white dark:bg-astro-secondary border border-astro-border dark:border-astro-strong-border text-xs space-y-1 shadow-2xs"
              >
                <div className="flex items-center justify-between font-bold">
                  <span className="text-astro-text dark:text-astro-muted">
                    House {b.house} ({b.sign})
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                    b.classification === 'SUPPORTING'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                      : b.classification === 'CONTRADICTING'
                      ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                      : 'bg-astro-secondary/15 text-astro-secondary dark:bg-amber-950/60 dark:text-amber-300'
                  }`}>
                    {b.classification}
                  </span>
                </div>
                <div className="text-[11px] text-astro-secondary-text dark:text-astro-muted flex justify-between">
                  <span>Lord: <strong className="text-astro-text dark:text-astro-muted">{b.lord}</strong> in H{b.lordPlacementHouse}</span>
                  <span className="font-semibold text-[#52635F] dark:text-amber-300">{b.lordDignity}</span>
                </div>
                <p className="text-[10px] text-astro-muted dark:text-astro-muted line-clamp-2 pt-0.5">
                  {isHi ? b.conditionSummaryHi : b.conditionSummaryEn}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Column 2: Planetary Strength, Shadbala & Karakas */}
        <div className="p-4 rounded-xl bg-astro-secondary-surface/70 dark:bg-astro-primary/40 border border-astro-border/80 dark:border-astro-strong-border/80 space-y-3">
          <div className="flex items-center gap-1.5 border-b border-astro-border dark:border-astro-strong-border pb-2">
            <Activity className="w-4 h-4 text-amber-500" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text dark:text-astro-muted">
              {isHi ? '२. षडबल व कारक सामर्थ्य' : '2. Shadbala & Karaka Dignity'}
            </h4>
          </div>

          <div className="space-y-2.5">
            {karakaAssessments.slice(0, 2).map((k, idx) => (
              <div
                key={idx}
                className="p-3 rounded-lg bg-white dark:bg-astro-secondary border border-astro-border dark:border-astro-strong-border text-xs space-y-1 shadow-2xs"
              >
                <div className="flex items-center justify-between font-bold">
                  <span className="text-astro-text dark:text-astro-muted">{k.name} ({k.sign})</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-astro-secondary/15 dark:bg-astro-secondary/50 text-astro-secondary dark:text-amber-300 font-semibold">
                    {k.karakaRole}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-astro-secondary-text dark:text-astro-muted">
                  <span>Dignity: <strong className="text-astro-secondary-text dark:text-astro-muted">{k.dignity}</strong></span>
                  <span className="font-mono">In House {k.house}</span>
                </div>
                <p className="text-[10px] text-astro-muted dark:text-astro-muted line-clamp-2 pt-0.5">
                  {isHi ? k.assessmentHi : k.assessmentEn}
                </p>
              </div>
            ))}

            {/* Shadbala Top Metrics Quick Chips */}
            <div className="p-2.5 rounded-lg bg-white/80 dark:bg-astro-secondary/80 border border-astro-border dark:border-astro-strong-border text-xs space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-astro-muted dark:text-astro-muted block">
                {isHi ? 'मुख्य ग्रह षडबल अनुपात:' : 'Key Planet Shadbala Ratios:'}
              </span>
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {shadbalaSummary.slice(0, 4).map((s) => (
                  <span
                    key={s.planet}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-[10px] text-astro-secondary-text dark:text-astro-muted font-mono font-medium"
                  >
                    <span>{s.planet}:</span>
                    <strong className={s.ratio >= 1.0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                      {s.ratio.toFixed(2)}x
                    </strong>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Column 3: SAV Bindus & Supporting Yogas */}
        <div className="p-4 rounded-xl bg-astro-secondary-surface/70 dark:bg-astro-primary/40 border border-astro-border/80 dark:border-astro-strong-border/80 space-y-3 md:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-1.5 border-b border-astro-border dark:border-astro-strong-border pb-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text dark:text-astro-muted">
              {isHi ? '३. अष्टकवर्ग व संमिलन योग' : '3. SAV Points & Confluence'}
            </h4>
          </div>

          <div className="space-y-2.5">
            {/* SAV House Bindu Summary */}
            <div className="p-3 rounded-lg bg-white dark:bg-astro-secondary border border-astro-border dark:border-astro-strong-border text-xs space-y-2 shadow-2xs">
              <span className="text-[11px] font-bold text-astro-text dark:text-astro-muted block">
                {isHi ? 'प्रासंगिक भाव सर्वाष्टकवर्ग बिंदु:' : 'Thematic Houses SAV Points:'}
              </span>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                {ashtakavargaSummary.slice(0, 4).map((av) => (
                  <div key={av.house} className="flex items-center justify-between p-1.5 rounded bg-astro-secondary-surface dark:bg-astro-secondary/60 font-mono">
                    <span className="text-astro-secondary-text dark:text-astro-muted">House {av.house}:</span>
                    <span className={`font-bold ${av.savPoints >= 28 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {av.savPoints} pts
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Supporting Confluence Factors */}
            <div className="p-3 rounded-lg bg-white dark:bg-astro-secondary border border-astro-border dark:border-astro-strong-border text-xs space-y-1.5 shadow-2xs">
              <span className="text-[11px] font-bold text-astro-text dark:text-astro-muted block">
                {isHi ? 'सकारात्मक संमिलन प्रमाण:' : 'Supportive Astrological Factors:'}
              </span>
              <ul className="space-y-1 text-[11px] text-astro-secondary-text dark:text-astro-muted">
                {supportingFactors.slice(0, 3).map((fact, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>{fact}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
